import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { map, take, filter, switchMap, catchError, tap, distinctUntilChanged, startWith } from 'rxjs/operators';

import { AppState, ConfigService, SentryLogger } from '@fcom/core';
import { Profile } from '@fcom/core-api/login';
import { profile, loginToken } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';

export enum TwoFactorAuthStatus {
  LOADING = 'loading',
  DISABLED = 'disabled',
  ENABLED = 'enabled',
}

export interface EnableGAuthValidatedResponse {
  secretKey: string;
}

export interface GetGAuthStatusResponse {
  secretKey?: string;
  validationCode: number;
  scratchCodes?: number[];
  smsAuthenticator: boolean;
  validated?: boolean;
}

export interface ValidateGAuthResponse {
  valid: boolean;
}

export enum TwoFactorAuthType {
  SMS = 'sms',
  APP = 'app',
}

const STATUS_PATH = 'getgauth';
const ENABLE_SMS_PATH = 'enablesmsauth';
const ENABLE_VALIDATED_PATH = 'enablegauthvalidated';
const VALIDATE_PATH = 'validategauth';
const DISABLE_PATH = 'disablegauth';

@Injectable()
export class TwoFactorAuthService {
  private updateStatus$ = new BehaviorSubject<'loading' | 'refresh'>('refresh');
  private memberNumber$: Observable<string>;

  status$: Observable<TwoFactorAuthStatus> = this.updateStatus$.pipe(
    distinctUntilChanged(),
    switchMap((updateStatus) => {
      if (updateStatus === 'loading') {
        return of(TwoFactorAuthStatus.LOADING);
      }

      return this.getStatus().pipe(
        map((status) => {
          if (status.smsAuthenticator || (status.secretKey && status.validated !== false)) {
            return TwoFactorAuthStatus.ENABLED;
          } else {
            return TwoFactorAuthStatus.DISABLED;
          }
        })
      );
    }),
    startWith(TwoFactorAuthStatus.LOADING),
    finShare()
  );

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private store$: Store<AppState>,
    private sentryLogger: SentryLogger
  ) {
    this.memberNumber$ = this.store$.pipe(
      profile(),
      take(1),
      map((memberProfile: Profile) => memberProfile.memberNumber)
    );
  }

  getStatus(): Observable<GetGAuthStatusResponse> {
    return this.gAuthRequest<GetGAuthStatusResponse>(STATUS_PATH);
  }

  enableSMSAuth(): Observable<unknown> {
    this.updateStatus$.next('loading');
    return this.gAuthRequest<unknown>(ENABLE_SMS_PATH).pipe(tap(() => this.updateStatus$.next('refresh')));
  }

  enableGAuthValidated(): Observable<EnableGAuthValidatedResponse> {
    return this.gAuthRequest<EnableGAuthValidatedResponse>(ENABLE_VALIDATED_PATH);
  }

  disableGAuth(): Observable<unknown> {
    this.updateStatus$.next('loading');
    return this.gAuthRequest<unknown>(DISABLE_PATH).pipe(tap(() => this.updateStatus$.next('refresh')));
  }

  getQRCodeImageUrl(secretKey: string): Observable<string> {
    return this.memberNumber$.pipe(
      map(
        (memberNumber) =>
          `https://${this.configService.cfg.casHost}/cas/gauth/qrgen?key=otpauth://totp/www.finnair.com${
            this.configService.cfg.casEnv === 'preprod' ? '(preprod)' : ''
          }:${memberNumber}?secret=${secretKey}&issuer=Finnair`
      )
    );
  }

  validateGAuth(securityCode: string): Observable<ValidateGAuthResponse> {
    this.updateStatus$.next('loading');
    return this.gAuthRequest<ValidateGAuthResponse>(`${VALIDATE_PATH}/${securityCode}`).pipe(
      tap(() => this.updateStatus$.next('refresh'))
    );
  }

  private gAuthRequest<ResponseType>(path: string): Observable<ResponseType> {
    const loginToken$ = this.store$.pipe(
      loginToken(),
      filter((t) => !!t),
      take(1)
    );
    return combineLatest([loginToken$, this.memberNumber$]).pipe(
      switchMap(([token, memberNumber]) => {
        const httpOptions = {
          headers: new HttpHeaders({
            oauth_token: token,
            'x-api-key': this.configService.cfg.loyaltyServices.memberService.gauthApiKey,
          }),
        };
        return this.http.get<ResponseType>(
          `${this.configService.cfg.loyaltyServices.memberService.gauthUrl}/${path}/${memberNumber}?_=${Date.now()}`,
          httpOptions
        );
      }),
      catchError((err: unknown) => {
        this.sentryLogger.warn('Error submitting GAuth request', { error: err });
        return throwError(() => err);
      })
    );
  }
}
